import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import * as Sentry from '@sentry/react';

import { withI18n } from 'spa/Components/Context';
import isValidException from 'utils/isValidException';

import Button, { ButtonThemeTypes } from '@urbanmassage/component-library/lib/Button/Button';
import Card from '@urbanmassage/component-library/lib/Icons/Card/Card';
import Input from '@urbanmassage/component-library/lib/Input/Input';

import './add-payment-method-form.less';

const isServer = typeof window === 'undefined';

const AddPaymentMethodForm = ({
  billing,
  hideCancelAdd,
  isProcessingForm,
  notifications,
  onAddNewPaymentMethod,
  onClose,
  tracker,
}, {i18n}) => {
  const [framesFormDisabled, setFramesFormDisabled] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [postcodeValue, setPostcodeValue] = useState(null);

  let form;
  let payButton;

  const handlePostcodeChange = (e) => {
    const { value } = e.target;

    setPostcodeValue(value);

    window.Frames.cardholder = {
      billingAddress: {
        zip: value,
      },
    };
  };

  const submitCard = (event) => {
    event.preventDefault();

    window.Frames.submitCard()
      .then((data) => {
        onAddNewPaymentMethod(data);

        window.Frames.enableSubmitForm();
      })
      .catch((error) => {
        notifications.add({
          id: 'add-payment-method-form-error',
          type: 'error',
          title: i18n.__error(error),
        });

        if (!i18n.isKnownError(error)) {
          tracker.track('Unknown error occurred', {
            category: 'Add Payment Method Form',
            error,
          });

          if (isValidException(error)) {
            Sentry.captureException(error);
          } else {
            Sentry.captureMessage('Add Payment Method Form error');
          }
        }
      });
  };

  useEffect(() => {
    form = document.getElementById('payment-form');
    payButton = document.getElementById('pay-button');

    if (!isServer && (process.env && process.env.NODE_ENV !== 'test')) {
      window.Frames.init({
        publicKey: billing.cardGateway.clientToken,
        cardValidationChanged: (event) => {
          // if all fields contain valid information, the Pay now
          // button will be enabled and the form can be submitted
          setFramesFormDisabled(!window.Frames.isCardValid());
        },
        removeAllEventHandlers: true,
        style: {
          base: {
            color: '#3C3C3C',
            height: '80px',
          },
          autofill: {
            backgroundColor: 'yellow',
          },
          focus: {
            border: '1px solid #FAC600',
          },
          invalid: {
            color: 'red',
          },
          placeholder: {
            base: {
              color: '#999999',
            },
          },
        },
      });

      form.addEventListener('submit', submitCard);
    }

    return () => {
      form.removeEventListener('submit', submitCard);
    };
  }, []);

  useEffect(() => {
    setButtonDisabled(framesFormDisabled || !postcodeValue || postcodeValue === '');
  }, [framesFormDisabled, postcodeValue]);

  return (
    <React.Fragment>
      <div>
        <form id="payment-form" method="POST" action="https://merchant.com/charge-card" data-testid="checkout-new-card-form">
          <div className="card-frame add-payment-method-form__form-frame-container" />
          <div className="add-payment-method-form__input-container">
            <div className="add-payment-method-form__separator" />
            <Input
              autocomplete="postal-code"
              label={i18n._x('Card Billing Postcode', 'Checkout address section')}
              name="card-postcode"
              onChange={handlePostcodeChange}
              required
              type="text"
            />
          </div>
          <Button
            assistiveLoadingText={i18n._x('Saving payment method', 'Add payment method button loading text')}
            block
            data-testid="add-payment-method-form-submit-button"
            disabled={buttonDisabled}
            id="pay-button"
            isLoading={isProcessingForm}
            notRounded
            preIcon={<Card />}
          >
            {isProcessingForm ? i18n._x('Saving..', 'Payment method submit text') : i18n._x('Add payment method', 'Payment method button text')}
          </Button>
        </form>
        {!hideCancelAdd && (
          <Button
            block
            data-testid="add-payment-method-form-close-button"
            disabled={isProcessingForm}
            notRounded
            onClick={onClose}
            type="button"
            theme={ButtonThemeTypes.transparent}
          >
            {i18n._x('Cancel add payment method', 'Add payment method close button')}
          </Button>
        )}
      </div>
    </React.Fragment>
  );
};

AddPaymentMethodForm.propTypes = {
  billing: PropTypes.object,
  hideCancelAdd: PropTypes.bool,
  isProcessingForm: PropTypes.bool.isRequired,
  notifications: PropTypes.shape({
    add: PropTypes.func.isRequired,
  }).isRequired,
  onAddNewPaymentMethod: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  tracker: PropTypes.shape({
    track: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRouter(withI18n(AddPaymentMethodForm));



// WEBPACK FOOTER //
// ./spa/Pages/BookingNext/Components/AddPaymentMethodForm/AddPaymentMethodForm.jsx